import { z } from "zod";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { motion } from "framer-motion";

import type { RootState } from "@/store";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useCreateProject } from "@/services/projectService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ImageUpload from "@/components/common/ImageUpload";
import DateTimePicker from "@/components/ui/DateTimePicker";
import {
  ArrowLeft,
  Plus,
  User,
  X,
  Info,
  DollarSign,
  MapPin,
  Target,
  TrendingUp,
  Image as ImageIcon,
  Users,
  FileText,
  AlertCircle,
  CheckCircle2,
  Flag,
  Building2,
  Clock,
  HelpCircle,
} from "lucide-react";

// Zod validation schema
const projectCreateSchema = z.object({
  status: z.string().min(1, "Status is required"),
  goal_amount: z.number().min(0, "Goal amount must be positive"),
  raised_amount: z.number().min(0, "Raised amount must be positive"),
  currency: z.string().min(1, "Currency is required"),
  featured: z.boolean(),
  hero_image: z.string().optional(),
  aim: z.string().optional(),
  current_state: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  location: z.string().optional(),
  category: z.string().optional(),
  coordinators: z
    .array(
      z.object({
        name: z.string().min(1, "Coordinator name is required"),
        role: z.string().min(1, "Coordinator role is required"),
        image: z.string().optional(),
        bio: z.string().optional(),
      })
    )
    .optional(),
  gallery: z.array(z.string()).optional(),
  translations: z
    .array(
      z.object({
        locale: z.string().min(1, "Locale is required"),
        title: z.string().min(1, "Title is required"),
        description: z.string().optional(),
        slug: z.string().min(1, "Slug is required"),
      })
    )
    .min(1, "At least one translation is required"),
});

type ProjectCreateFormData = z.infer<typeof projectCreateSchema>;

const ProjectCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const createProjectMutation = useCreateProject();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ProjectCreateFormData>({
    resolver: zodResolver(projectCreateSchema),
    defaultValues: {
      status: "planning",
      goal_amount: 0,
      raised_amount: 0,
      currency: "XAF",
      featured: false,
      hero_image: "",
      aim: "",
      current_state: "",
      start_date: "",
      end_date: "",
      location: "",
      category: "",
      coordinators: [],
      gallery: [],
      translations: [
        { locale: "en", title: "", description: "", slug: "" },
        { locale: "fr", title: "", description: "", slug: "" },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "translations",
  });

  const coordinators = watch("coordinators") || [];
  const [autoGenerateSlugs, setAutoGenerateSlugs] = useState<{
    [key: string]: boolean;
  }>({});

  // Auto-generate slug from title
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Auto-generate slugs when titles change
  const watchedTranslations = watch("translations");
  const translations = useMemo(
    () => watchedTranslations || [],
    [watchedTranslations]
  );
  const translationTitles = useMemo(
    () => translations.map((t) => t.title).join(","),
    [translations]
  );

  useEffect(() => {
    translations.forEach((translation, index) => {
      if (autoGenerateSlugs[translation.locale] && translation.title) {
        const newSlug = generateSlug(translation.title);
        if (translation.slug !== newSlug) {
          setValue(`translations.${index}.slug`, newSlug);
        }
      }
    });
  }, [translationTitles, autoGenerateSlugs, translations, setValue]);

  // Enable auto-generation when title is first entered
  const handleTitleChange = (index: number, locale: string, value: string) => {
    if (!autoGenerateSlugs[locale] && value) {
      setAutoGenerateSlugs((prev) => ({ ...prev, [locale]: true }));
    }
    setValue(`translations.${index}.title`, value);
  };

  const onSubmit = async (data: ProjectCreateFormData) => {
    setValidationError("");
    try {
      await createProjectMutation.mutateAsync(data);
      navigate(`/${currentLanguage}/admin/projects`);
    } catch (error) {
      console.error("Failed to create project:", error);
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          const firstError = Object.values(errors).flat()[0];
          setValidationError(firstError || t("admin.projects.errorCreating"));
        } else {
          setValidationError(t("admin.projects.errorCreating"));
        }
      } else if (error instanceof Error) {
        setValidationError(error.message || t("admin.projects.errorCreating"));
      } else {
        setValidationError(t("admin.projects.errorCreating"));
      }
    }
  };

  // Hero image upload handler
  const handleHeroImageUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          setValue("hero_image", result);
          resolve([result]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    [setValue]
  );

  const handleHeroImageChange = useCallback(
    (value: string | string[]) => {
      const imageUrl = Array.isArray(value) ? value[0] || "" : value || "";
      setValue("hero_image", imageUrl);
    },
    [setValue]
  );

  // Gallery upload handler
  const handleGalleryUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const base64Promises = files.map((file, index) => {
        return new Promise<{ base64: string; index: number }>(
          (resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const result = e.target?.result as string;
              resolve({ base64: result, index });
            };
            reader.onerror = (error) => {
              console.error(`Failed to read file ${file.name}:`, error);
              reject(error);
            };
            reader.readAsDataURL(file);
          }
        );
      });

      try {
        const results = await Promise.all(base64Promises);
        results.sort((a, b) => a.index - b.index);
        const base64Strings = results.map((r) => r.base64);

        setValue("gallery", [...(watch("gallery") || []), ...base64Strings]);
        return base64Strings;
      } catch (error) {
        console.error("Error processing gallery upload:", error);
        throw error;
      }
    },
    [setValue, watch]
  );

  const handleGalleryChange = useCallback(
    (urls: string | string[]) => {
      const urlArray = Array.isArray(urls) ? urls : [urls].filter(Boolean);
      setValue("gallery", urlArray);
    },
    [setValue]
  );

  const addCoordinator = () => {
    const currentCoordinators = watch("coordinators") || [];
    setValue("coordinators", [
      ...currentCoordinators,
      { name: "", role: "", image: "", bio: "" },
    ]);
  };

  const removeCoordinator = (index: number) => {
    const currentCoordinators = watch("coordinators") || [];
    const newCoordinators = currentCoordinators.filter((_, i) => i !== index);
    setValue("coordinators", newCoordinators);
  };

  // Coordinator image upload handler
  const handleCoordinatorImageUpload = useCallback(
    (index: number) =>
      async (files: File[]): Promise<string[]> => {
        const file = files[0];
        if (!file) return [];

        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            const currentCoordinators = watch("coordinators") || [];
            const updatedCoordinators = [...currentCoordinators];
            updatedCoordinators[index] = {
              ...updatedCoordinators[index],
              image: result,
            };
            setValue("coordinators", updatedCoordinators);
            resolve([result]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      },
    [setValue, watch]
  );

  const handleCoordinatorImageChange = useCallback(
    (index: number) => (value: string | string[]) => {
      const imageUrl = Array.isArray(value) ? value[0] || "" : value || "";
      const currentCoordinators = watch("coordinators") || [];
      const updatedCoordinators = [...currentCoordinators];
      updatedCoordinators[index] = {
        ...updatedCoordinators[index],
        image: imageUrl,
      };
      setValue("coordinators", updatedCoordinators);
    },
    [setValue, watch]
  );

  const addTranslation = () => {
    append({ locale: "", title: "", description: "", slug: "" });
  };

  const removeTranslation = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const [validationError, setValidationError] = useState("");

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <Button
            variant="outline"
            onClick={() => navigate(`/${currentLanguage}/admin/projects`)}
            className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("admin.projects.createNew")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.projects.createNewDescription")}
            </p>
          </div>
        </motion.div>

        {/* Validation Error */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 dark:text-red-400 flex-1">
              {validationError}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Project Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  {t("admin.projects.basicInfo")}
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t(
                    "admin.projects.basicInfoHelp",
                    "Enter the essential information about your project. Fields marked with * are required."
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="status"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Flag className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      {t("admin.projects.table.status")} *
                    </Label>
                    <select
                      {...register("status")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="planning">
                        {t("projects.status.planning")}
                      </option>
                      <option value="in_progress">
                        {t("projects.status.in_progress")}
                      </option>
                      <option value="completed">
                        {t("projects.status.completed")}
                      </option>
                      <option value="on_hold">
                        {t("projects.status.on_hold")}
                      </option>
                    </select>
                    {errors.status && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {errors.status.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="currency"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                      {t("admin.projects.table.currency")} *
                    </Label>
                    <Input
                      {...register("currency")}
                      placeholder="XAF"
                      className="w-full"
                    />
                    {errors.currency && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {errors.currency.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="goal_amount"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      {t("admin.projects.table.goalAmount")} *
                    </Label>
                    <Input
                      {...register("goal_amount", { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full"
                    />
                    {errors.goal_amount && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {errors.goal_amount.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="raised_amount"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      {t("admin.projects.table.raisedAmount")} *
                    </Label>
                    <Input
                      {...register("raised_amount", { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full"
                    />
                    {errors.raised_amount && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {errors.raised_amount.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox {...register("featured")} />
                  <Label htmlFor="featured" className="text-sm font-medium">
                    {t("admin.projects.table.featured")}
                  </Label>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <ImageIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  {t("admin.projects.heroImage")}
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.projects.heroImageHelp")}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUpload
                  onUpload={handleHeroImageUpload}
                  onChange={handleHeroImageChange}
                  value={watch("hero_image") || ""}
                  multiple={false}
                  maxFiles={1}
                  maxSize={10 * 1024 * 1024} // 10MB
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Project Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <FileText className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  {t("admin.projects.details")}
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t(
                    "admin.projects.detailsHelp",
                    "Provide detailed information about the project"
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-2">
                  <Label htmlFor="aim" className="text-sm font-semibold">
                    {t("admin.projects.aim")}
                  </Label>
                  <textarea
                    {...register("aim")}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={4}
                    placeholder={t("admin.projects.aimPlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="current_state"
                    className="text-sm font-semibold"
                  >
                    {t("admin.projects.currentState")}
                  </Label>
                  <textarea
                    {...register("current_state")}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={4}
                    placeholder={t("admin.projects.currentStatePlaceholder")}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="start_date"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      {t("admin.projects.startDate")}
                    </Label>
                    <DateTimePicker
                      id="start_date"
                      value={watch("start_date") || ""}
                      onChange={(value) => {
                        // Extract date part (YYYY-MM-DD) from ISO string (YYYY-MM-DDTHH:mm)
                        const dateOnly = value ? value.split("T")[0] : "";
                        setValue("start_date", dateOnly);
                      }}
                      placeholder={t("admin.projects.startDate")}
                      showTimeSelect={false}
                      dateFormat="MMM dd, yyyy"
                      maxDate={
                        watch("end_date")
                          ? new Date(watch("end_date")!)
                          : undefined
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="end_date"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      {t("admin.projects.endDate")}
                    </Label>
                    <DateTimePicker
                      id="end_date"
                      value={watch("end_date") || ""}
                      onChange={(value) => {
                        // Extract date part (YYYY-MM-DD) from ISO string (YYYY-MM-DDTHH:mm)
                        const dateOnly = value ? value.split("T")[0] : "";
                        setValue("end_date", dateOnly);
                      }}
                      placeholder={t("admin.projects.endDate")}
                      showTimeSelect={false}
                      dateFormat="MMM dd, yyyy"
                      minDate={
                        watch("start_date")
                          ? new Date(watch("start_date")!)
                          : undefined
                      }
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="location"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <MapPin className="w-4 h-4 text-green-600 dark:text-green-400" />
                      {t("admin.projects.location")}
                    </Label>
                    <Input
                      {...register("location")}
                      placeholder="City, Country"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="category"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Building2 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      {t("admin.projects.category")}
                    </Label>
                    <Input
                      {...register("category")}
                      placeholder="e.g., Infrastructure, Education"
                      className="w-full"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Project Coordinators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                    <Users className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                    {t("admin.projects.coordinators")}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCoordinator}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("admin.projects.addCoordinator")}
                  </Button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t(
                    "admin.projects.coordinatorsHelp",
                    "Add project coordinators and their information"
                  )}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {coordinators.map((_, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-4 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-900/50"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold flex items-center text-gray-900 dark:text-white">
                          <User className="w-5 h-5 mr-2 text-pink-600 dark:text-pink-400" />
                          {t("admin.projects.coordinator")} {index + 1}
                        </h4>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeCoordinator(index)}
                          className="hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label
                            htmlFor={`coordinators.${index}.name`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.projects.coordinatorName")} *
                          </Label>
                          <Input
                            {...register(`coordinators.${index}.name`)}
                            placeholder={t(
                              "admin.projects.coordinatorNamePlaceholder"
                            )}
                            className="w-full"
                          />
                          {errors.coordinators?.[index]?.name && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              {errors.coordinators[index]?.name?.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor={`coordinators.${index}.role`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.projects.coordinatorRole")} *
                          </Label>
                          <Input
                            {...register(`coordinators.${index}.role`)}
                            placeholder={t(
                              "admin.projects.coordinatorRolePlaceholder"
                            )}
                            className="w-full"
                          />
                          {errors.coordinators?.[index]?.role && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              {errors.coordinators[index]?.role?.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor={`coordinators.${index}.bio`}
                          className="text-sm font-semibold"
                        >
                          {t("admin.projects.coordinatorBio")}
                        </Label>
                        <textarea
                          {...register(`coordinators.${index}.bio`)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                          placeholder={t(
                            "admin.projects.coordinatorBioPlaceholder"
                          )}
                        />
                      </div>

                      {/* Coordinator Image Upload */}
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">
                          {t("admin.projects.coordinatorImage")}
                        </Label>
                        <ImageUpload
                          onUpload={handleCoordinatorImageUpload(index)}
                          onChange={handleCoordinatorImageChange(index)}
                          value={coordinators[index]?.image || ""}
                          multiple={false}
                          maxFiles={1}
                          maxSize={5 * 1024 * 1024} // 5MB
                        />
                      </div>
                    </motion.div>
                  ))}
                  {coordinators.length === 0 && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <p>{t("admin.projects.noCoordinators")}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Project Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <ImageIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  {t("admin.projects.gallery")}
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.projects.galleryHelp")}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUpload
                  onUpload={handleGalleryUpload}
                  onChange={handleGalleryChange}
                  value={watch("gallery") || []}
                  multiple={true}
                  maxFiles={20}
                  maxSize={5 * 1024 * 1024} // 5MB
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Translations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                    <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    {t("admin.projects.translations")}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTranslation}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("admin.projects.addTranslation")}
                  </Button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t(
                    "admin.projects.translationsHelp",
                    "Add translations for different languages"
                  )}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {fields.map((field, index) => {
                    const translation = translations[index];
                    const currentLocale =
                      translation?.locale || field.locale || "";
                    return (
                      <motion.div
                        key={field.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-4 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-900/50"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold">
                              {currentLocale.toUpperCase() || index + 1}
                            </span>
                            {currentLocale === "en"
                              ? "English"
                              : currentLocale === "fr"
                              ? "Français"
                              : t("admin.projects.translation")}{" "}
                            {index + 1}
                          </h4>
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeTranslation(index)}
                              className="hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor={`translations.${index}.locale`}
                              className="text-sm font-semibold"
                            >
                              {t("admin.projects.locale")} *
                            </Label>
                            <select
                              {...register(`translations.${index}.locale`)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="en">English</option>
                              <option value="fr">Français</option>
                            </select>
                            {errors.translations?.[index]?.locale && (
                              <p className="text-sm text-red-600 dark:text-red-400">
                                {errors.translations[index]?.locale?.message}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label
                              htmlFor={`translations.${index}.slug`}
                              className="flex items-center gap-2 text-sm font-semibold"
                            >
                              {t("admin.projects.slug")} *
                              <div title={t("admin.events.slugHelp")}>
                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                              </div>
                            </Label>
                            <div className="relative">
                              <Input
                                {...register(`translations.${index}.slug`)}
                                placeholder="project-slug"
                                className="w-full font-mono text-sm"
                                onChange={(e) => {
                                  setValue(
                                    `translations.${index}.slug`,
                                    e.target.value
                                  );
                                  // Disable auto-generation if user manually edits
                                  setAutoGenerateSlugs((prev) => ({
                                    ...prev,
                                    [currentLocale]: false,
                                  }));
                                }}
                              />
                              {autoGenerateSlugs[currentLocale] && (
                                <Badge
                                  variant="secondary"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
                                >
                                  {t("admin.events.autoGenerated")}
                                </Badge>
                              )}
                            </div>
                            {errors.translations?.[index]?.slug && (
                              <p className="text-sm text-red-600 dark:text-red-400">
                                {errors.translations[index]?.slug?.message}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor={`translations.${index}.title`}
                            className="flex items-center gap-2 text-sm font-semibold"
                          >
                            {t("admin.projects.title")} *
                            <div title={t("admin.events.titleHelp")}>
                              <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                            </div>
                          </Label>
                          <Input
                            {...register(`translations.${index}.title`)}
                            onChange={(e) =>
                              handleTitleChange(
                                index,
                                currentLocale,
                                e.target.value
                              )
                            }
                            placeholder={t("admin.projects.titlePlaceholder")}
                            className="w-full"
                          />
                          {errors.translations?.[index]?.title && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              {errors.translations[index]?.title?.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor={`translations.${index}.description`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.projects.description")}
                          </Label>
                          <textarea
                            {...register(`translations.${index}.description`)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows={4}
                            placeholder={t(
                              "admin.projects.descriptionPlaceholder"
                            )}
                          />
                          {errors.translations?.[index]?.description && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              {errors.translations[index]?.description?.message}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Form Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/admin/projects`)}
              className="px-6"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createProjectMutation.isPending}
              className="px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {createProjectMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("common.creating")}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {t("admin.projects.create")}
                </span>
              )}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

ProjectCreate.displayName = "ProjectCreate";

export default ProjectCreate;
